// Typed API client for Numaco Meet.
// Implements: architecture.md v1.0 section 5. Every method maps to one route in
// packages/server/src/app.ts and returns the server's response shape (or throws
// an ApiError carrying the HTTP status + the server's machine-readable code).
//
// The client is a thin, injectable wrapper over fetch: components import the
// default singleton; tests construct one with a mocked fetch. Admin/session is
// cookie-based (httpOnly), so we always send credentials: 'same-origin'.

import {
  ApiError,
  type AdminConfigResponse,
  type AvailabilityResponse,
  type BookingResponse,
  type BookingTypesResponse,
  type ContactRequest,
  type CreateBookingRequest,
  type CreateBookingResponse,
  type EventResponse,
  type HostConfig,
  type MeResponse,
  type PublicHostResponse,
  type RescheduleResponse,
} from './types.js';

export interface ApiClientOptions {
  /** Base path prefix for the API. Defaults to '' (same origin, /api/...). */
  baseUrl?: string;
  /** Injectable fetch (tests). Defaults to the global fetch. */
  fetchImpl?: typeof fetch;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Query params; undefined values are dropped. */
  query?: Record<string, string | undefined>;
  signal?: AbortSignal;
}

function buildUrl(
  baseUrl: string,
  path: string,
  query?: Record<string, string | undefined>,
): string {
  let url = `${baseUrl}${path}`;
  if (query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== '') params.set(k, v);
    }
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }
  return url;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: ApiClientOptions = {}) {
    this.baseUrl = opts.baseUrl ?? '';
    // Bind so a passed-in global fetch keeps its receiver.
    this.fetchImpl = opts.fetchImpl ?? ((...a) => fetch(...a));
  }

  private async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const url = buildUrl(this.baseUrl, path, opts.query);
    const init: RequestInit = {
      method: opts.method ?? 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    };
    if (opts.signal) init.signal = opts.signal;
    if (opts.body !== undefined) {
      init.headers = { ...init.headers, 'Content-Type': 'application/json' };
      init.body = JSON.stringify(opts.body);
    }

    let res: Response;
    try {
      res = await this.fetchImpl(url, init);
    } catch (err) {
      // Network-level failure (offline, DNS, CORS). Surface as a 0-status ApiError.
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      throw new ApiError(
        err instanceof Error ? err.message : 'Network request failed',
        0,
        'NetworkError',
      );
    }

    const text = await res.text();
    let parsed: unknown;
    try {
      parsed = text ? JSON.parse(text) : undefined;
    } catch {
      parsed = text;
    }

    if (!res.ok) {
      const body = parsed as { error?: string; message?: string } | undefined;
      const code = body?.error;
      const message = body?.message ?? body?.error ?? `Request failed (${res.status})`;
      throw new ApiError(message, res.status, code, parsed);
    }

    return parsed as T;
  }

  // ---------------- Public (host addressed by slug, ADR-0002 section 4) ----------------

  /** GET /api/hosts/:slug — the host's public profile (slug + displayName only). */
  getPublicHost(slug: string, signal?: AbortSignal): Promise<PublicHostResponse> {
    return this.request<PublicHostResponse>(
      `/api/hosts/${encodeURIComponent(slug)}`,
      signal ? { signal } : {},
    );
  }

  /** GET /api/hosts/:slug/booking-types */
  listBookingTypes(slug: string, signal?: AbortSignal): Promise<BookingTypesResponse> {
    return this.request<BookingTypesResponse>(
      `/api/hosts/${encodeURIComponent(slug)}/booking-types`,
      signal ? { signal } : {},
    );
  }

  /**
   * GET /api/hosts/:slug/events/:eventSlug — resolve one (possibly unlisted)
   * booking type by its event slug (ADR-0003 section 3). 404s a slug that does
   * not resolve to an event under this host.
   */
  getEvent(slug: string, eventSlug: string, signal?: AbortSignal): Promise<EventResponse> {
    return this.request<EventResponse>(
      `/api/hosts/${encodeURIComponent(slug)}/events/${encodeURIComponent(eventSlug)}`,
      signal ? { signal } : {},
    );
  }

  /** GET /api/hosts/:slug/availability?type=&from=&to=&tz= */
  getAvailability(
    slug: string,
    args: { type: string; from: string; to: string; tz?: string },
    signal?: AbortSignal,
  ): Promise<AvailabilityResponse> {
    return this.request<AvailabilityResponse>(
      `/api/hosts/${encodeURIComponent(slug)}/availability`,
      {
        query: { type: args.type, from: args.from, to: args.to, tz: args.tz },
        ...(signal ? { signal } : {}),
      },
    );
  }

  /** POST /api/hosts/:slug/bookings */
  createBooking(slug: string, req: CreateBookingRequest): Promise<CreateBookingResponse> {
    return this.request<CreateBookingResponse>(
      `/api/hosts/${encodeURIComponent(slug)}/bookings`,
      { method: 'POST', body: req },
    );
  }

  /** GET /api/bookings/:token (signed token) */
  getBooking(signedToken: string): Promise<BookingResponse> {
    return this.request<BookingResponse>(`/api/bookings/${encodeURIComponent(signedToken)}`);
  }

  /** POST /api/bookings/:token/cancel */
  cancelBooking(signedToken: string): Promise<BookingResponse> {
    return this.request<BookingResponse>(
      `/api/bookings/${encodeURIComponent(signedToken)}/cancel`,
      { method: 'POST' },
    );
  }

  /** POST /api/bookings/:token/reschedule */
  rescheduleBooking(signedToken: string, newStart: string): Promise<RescheduleResponse> {
    return this.request<RescheduleResponse>(
      `/api/bookings/${encodeURIComponent(signedToken)}/reschedule`,
      { method: 'POST', body: { newStart } },
    );
  }

  /**
   * POST /api/contact — the public "message Numaco" form. Resolves on success
   * (the server returns 204); throws an ApiError on a non-2xx (e.g. status 429
   * when rate-limited, 400 on validation, 500 on a send failure). Always send
   * `website` (empty for a human) so the server's honeypot is present.
   */
  sendContact(req: ContactRequest): Promise<void> {
    return this.request<void>('/api/contact', { method: 'POST', body: req });
  }

  // ---------------- Auth (Microsoft sign-in, ADR-0002 section 1) ----------------

  /** Absolute URL that starts the Microsoft (Azure AD) sign-in flow. */
  loginUrl(): string {
    return `${this.baseUrl}/api/auth/login`;
  }

  /** GET /api/me — the signed-in host, or throws ApiError(401) when no session. */
  getMe(): Promise<MeResponse> {
    return this.request<MeResponse>('/api/me');
  }

  /** POST /api/auth/logout — clears the session cookie. */
  logout(): Promise<{ ok: true }> {
    return this.request<{ ok: true }>('/api/auth/logout', { method: 'POST' });
  }

  // ---------------- Admin (session-guarded, signed-in host only) ----------------

  /** GET /api/admin/config (guarded) */
  getAdminConfig(): Promise<AdminConfigResponse> {
    return this.request<AdminConfigResponse>('/api/admin/config');
  }

  /** PUT /api/admin/config (guarded). The server forces the session hostId. */
  putAdminConfig(config: HostConfig): Promise<AdminConfigResponse> {
    return this.request<AdminConfigResponse>('/api/admin/config', {
      method: 'PUT',
      body: config,
    });
  }

  /**
   * Absolute URL to start the per-host Google connect flow (session-guarded).
   * Microsoft comes connected with sign-in, so only Google is connected here.
   */
  connectGoogleUrl(): string {
    return `${this.baseUrl}/api/connect/google`;
  }
}

/** Default singleton: same-origin, global fetch. Components import this. */
export const api = new ApiClient();
